/**
 * Форматирует дату в человекочитаемый текст на русском языке
 * @param {string|Date} date - Дата для форматирования
 * @returns {string} - Отформатированная строка
 */
export function formatRelativeTime(date) {
    if (!date) return 'Дата неизвестна';

    const now = new Date();
    const updatedDate = new Date(date);

    // Проверка на валидность даты
    if (isNaN(updatedDate.getTime())) {
        return 'Неверная дата';
    }

    const diffMs = now - updatedDate;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    // Меньше минуты
    if (diffSeconds < 60) {
        return 'Обновлено только что';
    }

    // Меньше часа
    if (diffMinutes < 60) {
        if (diffMinutes === 1) return 'Обновлено минуту назад';
        if (diffMinutes < 5) return `Обновлено ${diffMinutes} минуты назад`;
        if (diffMinutes < 20) return `Обновлено ${diffMinutes} минут назад`;
        return `Обновлено ${diffMinutes} минут назад`;
    }

    // Меньше суток
    if (diffHours < 24) {
        if (diffHours === 1) return 'Обновлено час назад';
        if (diffHours < 5) return `Обновлено ${diffHours} часа назад`;
        return `Обновлено ${diffHours} часов назад`;
    }

    // Меньше недели
    if (diffDays < 7) {
        if (diffDays === 1) return 'Обновлено вчера';
        if (diffDays < 5) return `Обновлено ${diffDays} дня назад`;
        return `Обновлено ${diffDays} дней назад`;
    }

    // Меньше месяца
    if (diffWeeks < 4) {
        if (diffWeeks === 1) return 'Обновлено неделю назад';
        if (diffWeeks < 5) return `Обновлено ${diffWeeks} недели назад`;
        return `Обновлено ${diffWeeks} недель назад`;
    }

    // Меньше года
    if (diffMonths < 12) {
        if (diffMonths === 1) return 'Обновлено месяц назад';
        if (diffMonths < 5) return `Обновлено ${diffMonths} месяца назад`;
        return `Обновлено ${diffMonths} месяцев назад`;
    }

    // Больше года
    if (diffYears === 1) return 'Обновлено год назад';
    if (diffYears < 5) return `Обновлено ${diffYears} года назад`;
    return `Обновлено ${diffYears} лет назад`;
}

/**
 * Вычисляет длительность от указанного времени до текущего момента
 * @param {string|Date} startDate - Время начала
 * @returns {string} - Длительность в формате HH:MM:SS
 */
export function calculateDuration(startDate) {
    if (!startDate) return '00:00:00';

    const start = new Date(startDate).getTime();
    const now = new Date().getTime();
    const diff = Math.max(0, now - start);

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Возвращает строку с количеством прошедших часов с правильным склонением
 * @param {string|Date} startDate - Время начала
 * @returns {string} - Например "2 часа", "5 часов"
 */
export function formatDurationInHours(startDate) {
    if (!startDate) return '';

    const start = new Date(startDate).getTime();
    const now = new Date().getTime();
    const diff = Math.max(0, now - start);
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours === 0) return 'Меньше часа';

    const words = ['час', 'часа', 'часов'];
    const n = Math.abs(hours) % 100;
    const n1 = n % 10;

    let word = words[2];
    if (n > 10 && n < 20) {
        word = words[2];
    } else if (n1 > 1 && n1 < 5) {
        word = words[1];
    } else if (n1 === 1) {
        word = words[0];
    }

    return `${hours} ${word}`;
}

/**
 * Форматирует дату в вид: "25 марта 2026 г."
 */
export function formatDate(date) {
    if (!date) return '';

    const updatedDate = new Date(date);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };

    return updatedDate.toLocaleDateString('ru-RU', options);
}

/**
 * Форматирует дату с временем в вид: "25 марта 2026 г. в 22:30"
 */
export function formatDateTime(date) {
    if (!date) return '';

    const updatedDate = new Date(date);
    const datePart = updatedDate.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' });
    const timePart = updatedDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

    return `${datePart} в ${timePart}`;
}
